import { useEffect, useMemo, useRef } from 'react';
import { PostPlatformSettingsMap } from 'fym-shared/src/music24/postPlatformSettings';
import { SerializedScheduledPost, SerializedSocialChannel, SocialPlatform } from '../../types';
import { ComposerDraft } from '../useComposerDraft';
import { TikTokSettingsState, useTikTokSettings } from '../tiktok/useTikTokSettings';
import { useYouTubeSettings, YouTubeSettingsState } from '../youtube/useYouTubeSettings';

/**
 * True when a post on this channel goes out through the platform API: the channel is connected
 * and the platform can sign in. A manual channel goes out as a post pack and needs no settings.
 */
export function channelPostsThroughApi(channel: SerializedSocialChannel | undefined): boolean {
  return (
    channel !== undefined &&
    channel.status === 'connected' &&
    channel.autoPublishAvailable &&
    channel.connection !== null
  );
}

interface UseComposerPlatformSettingsProps {
  accountId: number;
  draft: ComposerDraft;
  /** The post that the Composer edits (`?postUuid=`), once it has loaded. */
  editPost?: SerializedScheduledPost;
}

export interface ComposerPlatformSettings {
  tiktok: TikTokSettingsState;
  youtube: YouTubeSettingsState;
  /** The platforms whose settings section shows: selected and posting through the API. */
  sections: SocialPlatform[];
  /** The platforms in `sections` whose settings stop the post (not chosen yet, or refused). */
  blocking: SocialPlatform[];
  /** The settings to send. Only the platforms whose choice is complete are in it. */
  settings: PostPlatformSettingsMap | undefined;
}

/**
 * The per-platform settings of the Composer (design music24-composer-tiktok). Each platform that
 * needs the user's own choice has a section; this hook holds their state, says which ones stop the
 * post and builds the `platformSettings` of the request.
 */
export function useComposerPlatformSettings({
  accountId,
  draft,
  editPost,
}: UseComposerPlatformSettingsProps): ComposerPlatformSettings {
  const { platforms, usable, media } = draft;
  const channelOf = (platform: SocialPlatform) => usable.find((channel) => channel.platform === platform);
  const tiktokActive = platforms.includes('tiktok') && channelPostsThroughApi(channelOf('tiktok'));

  const tiktok = useTikTokSettings({
    accountId,
    isActive: tiktokActive,
    mediaFileId: media?.fileId,
    mediaDurationSec: media?.durationSec ?? undefined,
  });

  const youtubeActive = platforms.includes('youtube') && channelPostsThroughApi(channelOf('youtube'));
  const youtube = useYouTubeSettings({ accountId, isActive: youtubeActive });

  // The post that the Composer edits holds its own settings: they fill the form once.
  const isLoaded = useRef(false);
  const { load: loadTikTok } = tiktok;
  const { load: loadYouTube, setPrivacy: setYouTubePrivacy } = youtube;
  useEffect(() => {
    if (isLoaded.current || editPost === undefined) {
      return;
    }
    isLoaded.current = true;
    const stored = editPost.platformSettings;
    if (stored === null) {
      return;
    }
    if (editPost.platform === 'tiktok' && 'privacyLevel' in stored) {
      loadTikTok(stored);
    }
    if (editPost.platform === 'youtube' && 'privacyStatus' in stored) {
      loadYouTube(stored);
    }
  }, [editPost, loadTikTok, loadYouTube]);

  // The posting default of the YouTube channel (who can see the video) is the selected value of a
  // post that has no settings of its own, one time. The user can change it for this post. "Made for
  // kids" always starts with no answer. TikTok has no default: its privacy level starts empty.
  const youtubeDefault = channelOf('youtube')?.postingDefaults?.youtube?.privacyStatus ?? null;
  const isDefaultLoaded = useRef(false);
  useEffect(() => {
    if (isDefaultLoaded.current || !youtubeActive || youtubeDefault === null) {
      return;
    }
    isDefaultLoaded.current = true;
    if (editPost?.platformSettings === null || editPost === undefined) {
      setYouTubePrivacy(youtubeDefault);
    }
  }, [editPost, setYouTubePrivacy, youtubeActive, youtubeDefault]);

  const sections = useMemo<SocialPlatform[]>(
    () => [...(tiktokActive ? (['tiktok'] as const) : []), ...(youtubeActive ? (['youtube'] as const) : [])],
    [tiktokActive, youtubeActive]
  );
  const blocking = useMemo<SocialPlatform[]>(
    () => [
      ...(tiktokActive && !tiktok.isReady ? (['tiktok'] as const) : []),
      ...(youtubeActive && !youtube.isReady ? (['youtube'] as const) : []),
    ],
    [tiktok.isReady, tiktokActive, youtube.isReady, youtubeActive]
  );
  const settings = useMemo<PostPlatformSettingsMap | undefined>(() => {
    const map: PostPlatformSettingsMap = {};
    if (tiktokActive && tiktok.isReady && tiktok.settings !== null) {
      map.tiktok = tiktok.settings;
    }
    if (youtubeActive && youtube.isReady && youtube.settings !== null) {
      map.youtube = youtube.settings;
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }, [tiktok.isReady, tiktok.settings, tiktokActive, youtube.isReady, youtube.settings, youtubeActive]);

  return { tiktok, youtube, sections, blocking, settings };
}
